/**
 * Node Inspector tab table — the single source of truth for what tabs exist,
 * what order they appear in, and how each one is explained to the user.
 *
 * Separate from InspectorTabStrip.tsx so that file only exports components
 * (react-refresh/only-export-components).
 */
import type React from 'react';
import { ArrowLeftRight, Eye, Gavel, Microscope, ShieldAlert, Users, Wand2 } from 'lucide-react';
import type { InspectorTab } from './types';

export type InspectorTabDef = {
    id: InspectorTab;
    label: string;
    icon: React.ElementType;
    /** Plain-English answer to "what am I looking at?" — shown under the strip. */
    hint: string;
};

/** Ordered the way an investigation runs: identify → who → what → checks → analyse → act. */
export const INSPECTOR_TABS: InspectorTabDef[] = [
    {
        id: 'overview',
        label: 'Overview',
        icon: Eye,
        hint: 'Who this wallet is — risk score, entity attribution, balance, and first-seen date.',
    },
    {
        id: 'counterparties',
        label: 'Parties',
        icon: Users,
        hint: 'Who this wallet traded with. Every wallet it sent to or received from, grouped by known entity.',
    },
    {
        id: 'transfers',
        label: 'Transfers',
        icon: ArrowLeftRight,
        hint: 'The transaction history for this wallet. Loading more transfers also pulls those hops onto the canvas.',
    },
    {
        id: 'intelligence',
        label: 'Intel',
        icon: ShieldAlert,
        hint: 'External checks — OFAC sanctions, exposure breakdown, bridges, DEX swaps, and P2P presence.',
    },
    {
        id: 'analysis',
        label: 'Analysis',
        icon: Wand2,
        hint: 'Run the automated investigation on this wallet, plus graph-wide algorithms like pattern scan.',
    },
    {
        id: 'actions',
        label: 'Actions',
        icon: Gavel,
        hint: 'Case work — flag the wallet, add it to a watchlist, draft a legal notice, or export evidence.',
    },
    {
        id: 'forensic',
        label: 'Forensic',
        icon: Microscope,
        hint: 'Records an exchange disclosed under subpoena for this wallet — KYC leads and confirmed movements.',
    },
];

/** `activity` is a legacy alias kept so old persisted state still lands on Transfers. */
export function isTabActive(tab: InspectorTab, activeTab: InspectorTab): boolean {
    if (tab === 'transfers') return activeTab === 'transfers' || activeTab === 'activity';
    return tab === activeTab;
}
